const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    return new Date(value.seconds * 1000);
  }

  const text = String(value).trim();
  if (!text) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T00:00:00`)
    : new Date(text);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const dateParts = (date) => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate(),
});

export const formatEventDateRange = (event = {}, locale = undefined) => {
  const start = toDate(event.eventDate || event.startDate || event.date);
  const end = toDate(event.endDate || event.endDateTime);

  if (!start) return "Date TBD";

  const formatter = new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  if (!end || end.getTime() === start.getTime()) return formatter.format(start);

  const startParts = dateParts(start);
  const endParts = dateParts(end);
  if (
    startParts.year === endParts.year &&
    startParts.month === endParts.month
  ) {
    return `${new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(start)}–${endParts.day}, ${endParts.year}`;
  }

  if (startParts.year === endParts.year) {
    return `${new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(start)} – ${formatter.format(end)}`;
  }

  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

export const formatEventTimeRange = (event = {}, locale = undefined) => {
  if (!event.startTime && !event.endTime) return "";

  const formatTime = (value) => {
    const parsed = new Date(`1970-01-01T${value}`);
    return Number.isNaN(parsed.getTime())
      ? value
      : new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(parsed);
  };

  if (event.startTime && event.endTime) {
    return `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`;
  }

  return formatTime(event.startTime || event.endTime);
};

export const shouldShowEventRegistrationStatus = (event = {}) =>
  event.reservationEnabled === true ||
  ["opens_later", "closed"].includes(event.registrationStatus);

export const getEventLocationQuery = (event = {}) => {
  const structuredLocation = [event.locationName, event.address, event.city, event.state]
    .filter(Boolean)
    .join(", ");

  if (event.locationName || event.address) return structuredLocation;
  return event.locationText || structuredLocation;
};

export const getEventLocationText = (event = {}) => {
  const structuredLocation = getEventLocationQuery(event);
  return structuredLocation || event.locationText || "Location not set";
};

export const getEventMapPopupDetails = (event = {}) => ({
  title: event.title || "Community Event",
  category: event.category || "",
  organizer: event.organizerName || "",
  location: getEventLocationText(event),
  date: formatEventDateRange(event),
  time: formatEventTimeRange(event),
  description: event.description || "",
  sourceUrl: event.sourceUrl || "",
  reservationEnabled: event.reservationEnabled === true,
});
